<template>
  <div v-if="showBanner" class="consent-overlay" role="dialog" aria-modal="true" aria-label="Cookie Consent">
    <div class="consent-box" role="document">
      <button @click="closeBannerWithoutSaving" class="btn btn-close" aria-label="Close cookie consent banner">
        ✕
      </button>
      <h2>We Value Your Privacy</h2>
      <p>
        We use services like Google Tag Manager, Facebook Pixel, Google Maps, and Google reCAPTCHA v3 to improve your experience and protect our forms.
        You can manage what you're happy with below.
      </p>

      <div v-if="showPreferences" class="preferences">
        <div class="toggle">
          <label class="switch">
            <input type="checkbox" v-model="consentPrefs.gtm" />
            <span class="slider"></span>
          </label>
          <span>Google Tag Manager</span>
        </div>

        <div class="toggle">
          <label class="switch">
            <input type="checkbox" v-model="consentPrefs.facebook" />
            <span class="slider"></span>
          </label>
          <span>Facebook Pixel</span>
        </div>

        <div class="toggle">
          <label class="switch">
            <input type="checkbox" v-model="consentPrefs.maps" />
            <span class="slider"></span>
          </label>
          <span>Google Maps</span>
        </div>

        <div class="toggle">
          <label class="switch">
            <input type="checkbox" v-model="consentPrefs.recaptcha" />
            <span class="slider"></span>
          </label>
          <span>Google reCAPTCHA v3</span>
        </div>
      </div>

      <div class="actions">
        <button @click="acceptAll" class="btn btn-primary">Accept All</button>
        <button @click="declineAll" class="btn btn-secondary">Decline All</button>
        <button @click="togglePreferences" class="btn btn-tertiary">
          {{ showPreferences ? 'Save Preferences' : 'Manage Preferences' }}
        </button>
        
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const PIXEL_ID = import.meta.env.VITE_FACEBOOK_PIXEL_ID
const GTM = import.meta.env.VITE_GTM
const RECAPTCHA_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

const showBanner = ref(false)
const showPreferences = ref(false)

const consentPrefs = ref({
  gtm: false,
  facebook: false,
  maps: false,
  recaptcha: false
})

const togglePreferences = () => {
  if (showPreferences.value) {
    savePreferences()
  }
  showPreferences.value = !showPreferences.value
}

const savePreferences = () => {
  localStorage.setItem('consent_preferences', JSON.stringify(consentPrefs.value))
  const box = document.querySelector('.consent-overlay')
  if (box) box.classList.add('fade-out')

  setTimeout(() => {
    showBanner.value = false
  }, 400)

  activateServices()

  // 🔥 send updated prefs in the event
  window.dispatchEvent(new CustomEvent('consent-updated', { detail: consentPrefs.value }))
}

const acceptAll = () => {
  consentPrefs.value = { gtm: true, facebook: true, maps: true, recaptcha: true }
  savePreferences()
}

const declineAll = () => {
  consentPrefs.value = { gtm: false, facebook: false, maps: false, recaptcha: false }
  savePreferences()
}

const closeBannerWithoutSaving = () => {
  // Just hide the banner, don't save preferences
  showBanner.value = false
}

const activateServices = () => {
  const prefs = consentPrefs.value

  // Google Tag Manager
  if (prefs.gtm && GTM && !window.gtmInitialized) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: 'cookie_consent_granted' })
    const gtm = document.createElement('script')
    gtm.src = `https://www.googletagmanager.com/gtm.js?id=${GTM}`
    gtm.async = true
    document.head.appendChild(gtm)
    window.gtmInitialized = true
  }

  // Facebook Pixel
  if (prefs.facebook && PIXEL_ID && !window.fbPixelInitialized) {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      }
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      t = b.createElement(e)
      t.async = true
      t.src = v
      s = b.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')

    fbq('init', PIXEL_ID)
    fbq('track', 'PageView')

    window.fbPixelInitialized = true
  }

  // Google Maps
  if (prefs.maps) {
    localStorage.setItem('consent_preferences', JSON.stringify(consentPrefs.value))
    window.dispatchEvent(new Event('consent-updated'))
  }

  // Google reCAPTCHA v3 - ONLY LOAD IF CONSENT GIVEN
  if (prefs.recaptcha && RECAPTCHA_KEY && !window.recaptchaInitialized) {
    const recaptchaScript = document.createElement('script')
    recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`
    recaptchaScript.async = true
    recaptchaScript.defer = true
    recaptchaScript.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          // Fire event so forms can initialise without page reload
          window.dispatchEvent(new Event('recaptcha-ready'))
        })
      }
    }
    document.head.appendChild(recaptchaScript)
    window.recaptchaInitialized = true
  }
}

onMounted(() => {
  const saved = localStorage.getItem('consent_preferences')
  if (saved) {
    consentPrefs.value = JSON.parse(saved)
    activateServices()
  } else {
    showBanner.value = true
  }

  // Allow reopening from components
  window.addEventListener('openConsentBanner', () => {
    showBanner.value = true
    showPreferences.value = true
  })

  window.revokeConsent = () => {
    localStorage.removeItem('consent_preferences')
    showBanner.value = true
    showPreferences.value = true
  }
})
</script>
